/**
 * Utility for extracting structured time data for translation purposes.
 * Implements the same logic as humanTime.js but returns structured data instead of strings.
 * Used by translateHumanTime in coreTranslationHelpers.ts for proper i18n support.
 */

export type HumanTimeData = {
    type: 'justNow';
} | {
    type: 'time';
    value: number;
    unit: string;
    isPlural: boolean;
    isFromFuture: boolean;
};

/**
 * Get structured time data using the same logic as humanTime.js but returning structured data
 * This ensures we maintain exact compatibility with the original behavior
 */
export const getHumanTimeData = (seconds: number | Date): HumanTimeData => {
    // Convert Date to seconds for processing, matching humanTime logic
    let processedSeconds: number;
    if (seconds instanceof Date) {
        processedSeconds = Math.round((Date.now() - seconds.getTime()) / 1000);
    } else {
        processedSeconds = seconds;
    }
    
    const isFromFuture = processedSeconds < 0;
    const absSeconds = Math.abs(processedSeconds);

    // Time calculations matching humanTime.js exactly
    const times = [
        absSeconds / 60 / 60 / 24 / 365, // years
        absSeconds / 60 / 60 / 24 / 30,  // months
        absSeconds / 60 / 60 / 24 / 7,   // weeks
        absSeconds / 60 / 60 / 24,       // days
        absSeconds / 60 / 60,            // hours
        absSeconds / 60,                 // minutes
        absSeconds,                      // seconds
    ];
    const units = ['year', 'month', 'week', 'day', 'hour', 'min', 'sec'];

    for (let i = 0; i < units.length; i++) {
        const time = Math.floor(times[i]);

        if (time >= 1) {
            return {
                type: 'time',
                value: time,
                unit: units[i],
                isPlural: time > 1, // Same logic as original humanTime.js
                isFromFuture,
            };
        }
    }

    return { type: 'justNow' };
};
